import React, { useRef } from "react";
import MyTaskList from "./MyTaskList/MyTaskList";
import MyIndividualTaskList from "../MyIndividualTaskListManagement/MyIndividualTaskList/MyIndividualTaskList";
import Accordion from "react-bootstrap/Accordion";
import "./MyTaskListManagement.css";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";

/**
 * MyTaskListManagement Component
 * Manages and displays two types of tasks in collapsible accordion sections:
 * 1. Daily Operational Tasks (Individual tasks)
 * 2. SOP Related Tasks
 * Each section includes a refresh button for manual data updates
 */
const MyTaskListManagement = () => {
  const { t } = useTranslation();
  
  // Refs to access child component methods for refreshing data
  const individualTaskListRef = useRef(null);
  const sopTaskListRef = useRef(null);

  /**
   * Refreshes the daily operational tasks
   * Calls the refresh method exposed by MyIndividualTaskList component
   */
  const handleRefreshDailyTasks = () => {
    if (
      individualTaskListRef.current?.refreshTaskList
    ) {
      individualTaskListRef.current.refreshTaskList();
    }
  };

  /**
   * Refreshes the SOP related tasks
   * Calls the refresh method exposed by MyTaskList component
   */
  const handleRefreshSopTasks = () => {
    if (
      sopTaskListRef.current?.refreshSopList
    ) {
      sopTaskListRef.current.refreshSopList();
    }
  };

  /**
   * Reusable refresh button component
   */
  const RefreshButton = ({ onClick, tooltipText }) => (
    <button
      onClick={(e) => {
        e.stopPropagation(); // Prevent accordion toggle when clicking refresh
        onClick();
      }}
      style={{
        background: "#1a7a73",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        color: "white",
        marginLeft: "10px",
        padding: "6px 10px",
        borderRadius: "4px",
        transition: "background-color 0.2s ease",
      }}
      title={tooltipText}
      onMouseOver={(e) => {
        e.target.style.backgroundColor = "#3a7a94";
      }}
      onMouseOut={(e) => {
        e.target.style.backgroundColor = "#1a7a73";
      }}
    >
      <RefreshCw size={16} />
    </button>
  );

  /**
   * Accordion header with title and refresh button
   */
  const AccordionHeaderContent = ({ title, onRefresh, tooltipText }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <span style={{ fontWeight: "500" }}>{title}</span>
      <RefreshButton 
        onClick={onRefresh} 
        tooltipText={tooltipText}
      />
    </div>
  );

  return (
    <div className="task-list-management-container">
      <Accordion defaultActiveKey="0" flush>
        {/* Daily Operational Tasks Section */}
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <AccordionHeaderContent
              title={t("daily_operational_task_header")}
              onRefresh={handleRefreshDailyTasks}
              tooltipText={t("refresh_daily_tasks_tooltip") || "Refresh Daily Tasks"}
            />
          </Accordion.Header>
          <Accordion.Body className="accordion-body-style">
            <MyIndividualTaskList ref={individualTaskListRef} />
          </Accordion.Body>
        </Accordion.Item>

        {/* SOP Related Tasks Section */}
        <Accordion.Item eventKey="1">
          <Accordion.Header>
            <AccordionHeaderContent
              title={t("sop_related_task_header")}
              onRefresh={handleRefreshSopTasks}
              tooltipText={t("refresh_sop_tasks_tooltip") || "Refresh SOP Tasks"}
            />
          </Accordion.Header>
          <Accordion.Body className="accordion-body-style">
            <MyTaskList ref={sopTaskListRef} />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default MyTaskListManagement;